<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="benchmark-range"
    />
    <ChartContent
      height="119px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBenchmarkChart"
      class="px-0 pt-4 pb-0"
    >
      <BarChart :id="`${widgetId}-chart`" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CanvasDrawBenchmarkPlugin } from '@/api/helpers/insights/chartPlugins';
import {
  basicChartTooltipPluginConfig,
  benchmarkPluginNumericOffset,
  hiringReportXScaleOptions,
  hiddenYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';

const TIME_TO_HIRE_BENCHMARK_MIN = 30;
const TIME_TO_HIRE_BENCHMARK_MAX = 50;

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'Time to Hire vs Benchmark';

const widgetId = 'time-to-hire-vs-benchmark';

const {
  brandPrimary,
  yellow500,
  textDarkest,
  white,
} = useColors();

const timeToHireMax = computed(() => {
  const { median_hire_time_days: medianHireTimeDays } = props.data || {};

  const maxValueToNextFifty = Math.ceil(medianHireTimeDays / 50) * 50;

  return maxValueToNextFifty > TIME_TO_HIRE_BENCHMARK_MAX
    ? maxValueToNextFifty
    : TIME_TO_HIRE_BENCHMARK_MAX;
});

const noWidgetData = computed(() => {
  const { median_hire_time_days: medianHireTimeDays } = props.data || {};

  return !medianHireTimeDays;
});

const chartData = computed(() => {
  const { median_hire_time_days: medianHireTimeDays } = props.data || {};

  return {
    labels: ['Time to Hire'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        borderColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          medianHireTimeDays,
        ],
      },
    ],
  };
});

const plugins = computed(() => [
  ChartDataLabels,
  CanvasDrawBenchmarkPlugin({
    benchmarkOptions: {
      min: TIME_TO_HIRE_BENCHMARK_MIN,
      max: TIME_TO_HIRE_BENCHMARK_MAX,
      ...benchmarkPluginNumericOffset,
    },
    scaleMax: timeToHireMax.value,
    chartId: `${widgetId}-chart`,
  }),
]);

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: basicChartTooltipPluginConfig(chartData, widgetId),
    datalabels: {
      color: function colorCallback(context) {
        const max = timeToHireMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        return percentOfSpace < 20 ? textDarkest.value : white.value;
      },
      anchor: 'end',
      align: function alignCallback(context) {
        const max = timeToHireMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        if (Number.isNaN(percentOfSpace)) return 'end';

        return percentOfSpace < 20 ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return `${value || 0} day${value !== 1 ? 's' : ''}`;
      },
    },
  },
  scales: {
    x: hiringReportXScaleOptions(timeToHireMax.value),
    y: hiddenYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 137px !important;
  max-height: 137px !important;
  padding: 0px 0px 16px !important;
  margin: 0 !important;

   svg {
    margin-bottom: 16px !important;
  }
}
</style>
